import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Shield, BarChart3, Megaphone, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type CookiePreferences = {
  essential: boolean; // Always true
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_CONSENT_KEY = 'bosphorus-cookie-consent';

export function CookieConsent() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    functional: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const handleRejectOptional = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    savePreferences(essentialOnly);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
    }));
    setIsVisible(false);

    // Here you would typically initialize/remove analytics based on preferences
    if (prefs.analytics) {
      // Enable Google Analytics, Yandex Metrica, etc.
      console.log('Analytics enabled');
    }
    if (prefs.marketing) {
      // Enable Facebook Pixel, etc.
      console.log('Marketing cookies enabled');
    }
  };

  if (!isVisible) return null;

  const cookieTypes = [
    {
      id: 'essential',
      icon: Shield,
      required: true,
    },
    {
      id: 'functional',
      icon: Settings,
      required: false,
    },
    {
      id: 'analytics',
      icon: BarChart3,
      required: false,
    },
    {
      id: 'marketing',
      icon: Megaphone,
      required: false,
    },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}} // Prevent closing by clicking backdrop
      />

      {/* Banner */}
      <div className="relative w-full max-w-2xl bg-bg-card border border-border-default rounded-2xl shadow-2xl pointer-events-auto mb-4 overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Cookie className="text-accent" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {t('cookieConsent.title')}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                {t('cookieConsent.description')}{' '}
                <Link to="/cookies" className="text-accent hover:underline" onClick={() => setIsVisible(false)}>
                  {t('cookieConsent.cookiePolicyLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Details Panel */}
        {showDetails && (
          <div className="px-6 pb-4">
            <div className="bg-bg-surface rounded-xl border border-border-default p-4 space-y-3">
              {cookieTypes.map((cookie) => (
                <div key={cookie.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                      <cookie.icon className="text-accent" size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">
                          {t(`cookieConsent.types.${cookie.id}.name`)}
                        </span>
                        {cookie.required && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/30">
                            {t('cookieConsent.required')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-tertiary">
                        {t(`cookieConsent.types.${cookie.id}.description`)}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[cookie.id as keyof CookiePreferences]}
                      disabled={cookie.required}
                      onChange={(e) => {
                        if (!cookie.required) {
                          setPreferences(prev => ({
                            ...prev,
                            [cookie.id]: e.target.checked,
                          }));
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className={`
                      w-11 h-6 rounded-full peer
                      ${cookie.required ? 'bg-green-500' : 'bg-bg-elevated peer-checked:bg-accent'}
                      peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent/50
                      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                      after:bg-white after:rounded-full after:h-5 after:w-5
                      after:transition-all peer-checked:after:translate-x-full
                      ${cookie.required ? 'opacity-70 cursor-not-allowed' : ''}
                    `} />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-6 pt-2 flex flex-col sm:flex-row gap-3">
          {!showDetails ? (
            <>
              <button
                onClick={handleRejectOptional}
                className="flex-1 px-4 py-3 rounded-xl border border-border-default text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors text-sm font-medium"
              >
                {t('cookieConsent.buttons.rejectOptional')}
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 px-4 py-3 rounded-xl border border-border-default text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors text-sm font-medium"
              >
                {t('cookieConsent.buttons.managePreferences')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-3 rounded-xl bg-accent hover:bg-accent-hover text-white transition-colors text-sm font-medium"
              >
                {t('cookieConsent.buttons.acceptAll')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowDetails(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-border-default text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors text-sm font-medium"
              >
                {t('cookieConsent.buttons.back')}
              </button>
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-4 py-3 rounded-xl bg-accent hover:bg-accent-hover text-white transition-colors text-sm font-medium"
              >
                {t('cookieConsent.buttons.savePreferences')}
              </button>
            </>
          )}
        </div>

        {/* KVKK Note */}
        <div className="px-6 pb-4">
          <p className="text-xs text-text-muted text-center">
            {t('cookieConsent.kvkkNote')}{' '}
            <Link to="/privacy#kvkk" className="text-accent hover:underline" onClick={() => setIsVisible(false)}>
              {t('cookieConsent.kvkkLink')}
            </Link>{' '}
            {t('cookieConsent.kvkkUnder')}
          </p>
        </div>
      </div>
    </div>
  );
}
